import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatTimecode, timecodeAt, timecodeSpec } from "@infoflyt/shared/timecode";
import type { Oekt } from "../state/session";
import * as nettlogg from "../state/nettlogg";
import * as knapper from "../state/hurtigknapper";
import * as deltakere from "../state/deltakere";
import { medSisteOrd, medStoreForkortelser } from "@infoflyt/shared/logg";
import { useLevende } from "../state/levende";
import * as avslutt from "../state/avsluttdagen";

/**
 * Livelogg: logging fra nettflata.
 *
 * ========  ÉN FLATE DER APPEN HAR TO  ========
 *
 * Appen deler Logg og Hurtiglogg i to skjermer, og den delingen er
 * ERGONOMISK og ikke strukturell — se den ufravikelige regelen: «to
 * skjermer, én logg». På en telefon er skjermen for liten til å ha både et
 * rutenett av knapper og et skrivefelt framme samtidig, og en finger som
 * leter etter KLAPP skal ikke måtte treffe mellom to tekstlinjer.
 *
 * En PC har plass. Da faller grunnen til delingen bort, og de to blir det
 * de alltid har vært i datamodellen: én logg. Knappene ligger på en rad rett
 * over skrivefeltet, der hånda allerede er. Bestilt 26.10.2026.
 *
 * ========  SAMME LOGG SOM REPORTERENS  ========
 *
 * Postene havner i den innloggedes egen økt for dagen — den samme synken
 * skriver i. `nettlogg.ts` er det ene stedet som vet hvordan en loggpost fra
 * nett blir til, og `CLAUDE.md` gjelder uendret: ingen skriver i en annen
 * manns logg.
 *
 * ========  TIDSKODEN FANGES VED TRYKKET  ========
 *
 * Avlesningen tas i det knappen trykkes eller «Ferdig» treffes, ikke når
 * raden lagres. Teksten skrives ofte lenge etterpå, og den skal ikke flytte
 * markøren. Det er den ufravikelige regelen, og den gjelder her som i appen.
 */

/** Hvor lenge man må holde inne for å få fram papirkurvene. Som i appen. */
const HOLD_MS = 500;

function tidskode(anker: nettlogg.Anker, p: nettlogg.Loggpost): string {
  const rate = anker.framerate as Parameters<typeof timecodeSpec>[0];
  const spec = timecodeSpec(rate, anker.dropFrame);
  const t = timecodeAt(
    {
      tcFrames: anker.tcFrames,
      monotonicMs: anker.monotonicMs,
      bootId: anker.bootId,
      wallUtcMs: anker.wallUtcMs,
      framerate: rate,
      dropFrame: anker.dropFrame,
      source: "manual",
    },
    { monotonicMs: p.monotonicMs, bootId: p.bootId, wallUtcMs: p.wallUtcMs },
    spec,
  );
  return formatTimecode(t.frames, spec);
}

export function LiveloggScreen({ oekt, iDag = true }: {
  readonly oekt: Oekt;
  /**
   * Staar vi paa dagens opptaksdag?
   *
   * ========  MAN LOGGER IKKE PAA EN ANNEN DAG  ========
   *
   * Dagpilene i toppstripa flytter hele flata, og liveloggen skrev rett
   * inn i den dagen man sto paa. Men posten baerer `wall_utc` fra NAA og
   * regnes mot DAGENS anker — en post skrevet klokka ti i dag, lagt i
   * gaarsdagens oekt, er en tidskode ingen kan stole paa.
   *
   * Appen har alltid hatt denne grensen: «blar man til en annen dag inne i
   * loggen, er det en VISNING — det flytter ikke oekten.» Her ble den
   * synlig 02.11.2026, da et trykk paa en pil tok bort tidskoden og tomte
   * lista uten at noen forsto hvorfor.
   */
  readonly iDag?: boolean;
}): JSX.Element {
  const [anker, setAnker] = useState<nettlogg.Anker | null>(null);
  const [oektId, setOektId] = useState<string | null>(null);
  const [poster, setPoster] = useState<readonly nettlogg.Loggpost[]>([]);
  const [klar, setKlar] = useState(false);
  const [feil, setFeil] = useState("");
  const [utkast, setUtkast] = useState("");
  /* Deltakernes forkortelser. «chr» rettes til «CHR» mens det skrives —
     regelen ligger i @infoflyt/shared/logg, delt med appen. */
  const [forkortelser, setForkortelser] = useState<readonly string[]>([]);
  const [knappene, setKnappene] = useState<readonly knapper.Hurtigknapp[]>([]);
  const [nyKnapp, setNyKnapp] = useState("");
  const [slettemodus, setSlettemodus] = useState(false);
  const trykk = useRef<{ timer: number; move: (e: PointerEvent) => void } | null>(null);
  const rull = useRef<HTMLDivElement | null>(null);
  /**
   * Avlesningen fra hurtigknappen man holder inne.
   *
   * TIDSKODEN FANGES I TRYKKET, men posten skrives naar fingeren slipper —
   * ellers hadde et langt trykk (som aapner papirkurvene) ogsaa satt en
   * markoer. Verdien er fra `pointerdown`, og det er den som gjelder.
   */
  const knappLest = useRef<ReturnType<typeof nettlogg.naa> | null>(null);
  /** Posten som rettes naa, og teksten slik den staar i feltet. */
  const [retter, setRetter] = useState<{ id: string; tekst: string } | null>(null);
  const [lagrer, setLagrer] = useState(false);
  /** Rapportene som alt er levert for oekta. Streken tegnes av dem. */
  const [levert, setLevert] = useState<readonly avslutt.Levert[]>([]);

  const produksjonId = oekt.production.id;
  const medlemId = oekt.member.id;
  const opptaksdag = oekt.shootDay;
  /* Teamet avgjoer hvilken oekt man skriver i: er man paa et team, deler
     hele teamet logg og rapport. Se state/nettlogg.ts. */
  const teamId = oekt.member.unitId ?? null;

  const spec = useMemo(
    () => timecodeSpec(oekt.production.framerate, oekt.production.dropFrame),
    [oekt.production.framerate, oekt.production.dropFrame],
  );

  const last = useCallback(async (): Promise<void> => {
    const a = await nettlogg.ankerFor(
      produksjonId, opptaksdag, medlemId, spec,
      oekt.production.framerate, oekt.production.dropFrame,
    );
    setAnker(a);
    const id = await nettlogg.finnOekt(produksjonId, opptaksdag, medlemId, teamId);
    setOektId(id);
    setPoster(id === null ? [] : await nettlogg.hentPoster(id));
    /* «En levert rapport setter en strek i loggen.» Streken tegnes av
       rapportradene — se state/avsluttdagen.ts. */
    setLevert(await avslutt.hentLevert(id));
    setKlar(true);
  }, [produksjonId, opptaksdag, medlemId, teamId, spec,
    oekt.production.framerate, oekt.production.dropFrame]);

  useEffect(() => { void last(); }, [last]);
  /* Redaktoerens tre er med: hjertet, kommentaren og synkspoersmaalet skal
     komme fortloepende, som de gjoer i appen. Brukeren 02.11.2026. */
  useLevende(
    "livelogg",
    ["log_entry", "tc_anchor", "entry_comment", "entry_mark", "su_question", "report"],
    last);

  useEffect(() => {
    setKnappene(knapper.les(produksjonId, medlemId));
  }, [produksjonId, medlemId]);

  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      const d = await deltakere.hent(produksjonId);
      if (!avbrutt) setForkortelser(d.map((x) => x.forkortelse));
    })();
    return () => { avbrutt = true; };
  }, [produksjonId]);

  /*
   * RULLER TIL BUNNEN NÅR NOE KOMMER.
   *
   * Loggen leses nedover og det ferskeste står nederst — som i appen, og som
   * i redaktørens loggkort.
   *
   * ⚠ `scrollTop`, ALDRI `scrollIntoView`. Den siste ruller HVER rullbar
   * forelder for å få elementet fram, og en av dem er pageren — som ruller
   * VANNRETT mellom flatene. Resultatet var at hele skjermen sto forskjøvet
   * til siden med halve flata utenfor, uten at noe pekte på loggen. Samme
   * grep som `Samtale` i LoggScreen bruker, og av samme grunn. Rettet
   * 26.10.2026.
   */
  useEffect(() => {
    const el = rull.current;
    if (el === null) return;
    el.scrollTop = el.scrollHeight;
  }, [poster.length]);

  const rammer = useMemo((): nettlogg.Rammer | null => {
    if (anker === null) return null;
    return {
      oektId,
      produksjonId,
      opptaksdag,
      ankerId: anker.id,
      ankerBootId: anker.bootId,
      medlemId,
      teamId,
      forfatterNavn: oekt.member.displayName,
    };
  }, [anker, oektId, produksjonId, opptaksdag, medlemId, teamId,
    oekt.member.displayName]);

  const skriv = useCallback(async (
    felt: { label: string; farge: string | null; tekst: string },
    lest = nettlogg.naa(),
  ): Promise<void> => {
    if (rammer === null || !iDag) return;
    setFeil("");
    const svar = await nettlogg.skrivPost(rammer, felt, lest);
    if (svar === null) { setFeil("Fikk ikke lagret posten. Prøv igjen."); return; }
    setOektId(svar.oektId);
    setPoster((p) => [...p, svar.post]);
  }, [rammer, iDag]);

  /*
   * MARKØREN SETTES I TRYKKET, og avlesningen tas FØR noe annet skjer.
   *
   * «Tidskode fanges ved knappetrykk, ikke ved lagring» — en rundtur til
   * basen tar hundrevis av millisekunder, og det er frames.
   */
  const markoer = useCallback((k: knapper.Hurtigknapp): void => {
    const lest = knappLest.current ?? nettlogg.naa();
    knappLest.current = null;
    void skriv({ label: k.etikett, farge: k.farge, tekst: "" }, lest);
  }, [skriv]);

  const ferdig = useCallback((): void => {
    /* Og det siste ordet, naar posten faktisk skrives. */
    const tekst = medSisteOrd(utkast, forkortelser).trim();
    if (tekst === "") return;
    /* Avlesningen tas her, ikke i `skriv`: teksten kan ha stått en stund. */
    const lest = nettlogg.naa();
    setUtkast("");
    void skriv({ label: "", farge: null, tekst }, lest);
  }, [utkast, skriv, forkortelser]);

  const leggTilKnapp = useCallback((): void => {
    const etikett = nyKnapp.trim();
    if (etikett === "") return;
    const ny: knapper.Hurtigknapp = {
      id: crypto.randomUUID(),
      etikett,
      farge: knapper.nesteFarge(knappene.length),
    };
    const neste = [...knappene, ny];
    setKnappene(neste);
    knapper.skriv(produksjonId, medlemId, neste);
    setNyKnapp("");
  }, [nyKnapp, knappene, produksjonId, medlemId]);

  const fjernKnapp = useCallback((id: string): void => {
    const neste = knappene.filter((k) => k.id !== id);
    setKnappene(neste);
    knapper.skriv(produksjonId, medlemId, neste);
  }, [knappene, produksjonId, medlemId]);

  /* ---- retting: fyll ut en post du alt har sendt ----------------------- */

  /*
   * ========  TEKSTEN SKRIVES NAAR MAN HAR TID  ========
   *
   * Brukeren 02.11.2026: «om det er hektisk naar man jobber, saa lagrer man
   * bare postene og utfyller de foer man sender rapporten.» Tidskoden ble
   * fanget i trykket og roeres ikke — det er bare teksten som kommer etter.
   *
   * BARE EGNE POSTER, OG BARE DE SOM BLE SKREVET HER. Et team deler oekt, saa
   * lista inneholder ogsaa postene kollegene skrev paa telefonen — og dine
   * egne derfra. Originalen deres ligger paa telefonen, og en retting her
   * ville blitt overskrevet ved neste synk. Basen holder den samme grensen
   * (`endre_loggtekst`), dette er bare flata som ikke tilbyr det.
   */
  const kanRette = useCallback((p: nettlogg.Loggpost): boolean =>
    p.forfatterId === medlemId && p.opphav === "web", [medlemId]);

  const lagre = useCallback(async (): Promise<void> => {
    const r = retter;
    if (r === null) return;
    const post = poster.find((x) => x.id === r.id);
    const tekst = medSisteOrd(r.tekst, forkortelser).trim();
    if (post === undefined || tekst === post.tekst) { setRetter(null); return; }
    setLagrer(true);
    const svar = await nettlogg.endreTekst(r.id, tekst);
    setLagrer(false);
    if ("feil" in svar) { setFeil(svar.feil); return; }
    setFeil("");
    setRetter(null);
    setPoster((liste) => liste.map((x) =>
      (x.id === r.id ? { ...x, tekst: svar.tekst } : x)));
  }, [retter, poster, forkortelser]);

  /*
   * «Avslutt dagen» LIGGER I MENYEN, ikke her.
   *
   * Den sto nederst paa loggen i ett doegn. Brukeren 03.11.2026: «Jeg
   * oensker heller at avslutt dagen skal ligge i menyen, slik den gjoer i
   * appen, istedenfor aa ha den under liveloggen.» Loggen er flata man
   * skriver paa hele dagen, og det som avslutter den skal ikke ligge i
   * veien for den. Se screens/AvsluttScreen.tsx.
   *
   * Det som blir igjen her er STREKEN: en levert rapport er et
   * oeyeblikksbilde, og det som skrives etterpaa er ikke med i den.
   */

  /* ---- slettemodus: samme gest som i appen ----------------------------- */

  const avbrytTrykk = useCallback((): void => {
    const t = trykk.current;
    if (t === null) return;
    window.clearTimeout(t.timer);
    document.removeEventListener("pointermove", t.move);
    trykk.current = null;
  }, []);

  const startTrykk = useCallback((e: React.PointerEvent): void => {
    if (slettemodus || trykk.current !== null) return;
    const x = e.clientX;
    const y = e.clientY;
    /* Bevegelsen leses fra DOKUMENTET: en rullende boks kan ta pekeren med
       `setPointerCapture`, og da ser posten aldri at fingeren flyttet seg. */
    const move = (ev: PointerEvent): void => {
      if (Math.abs(ev.clientY - y) > 8 || Math.abs(ev.clientX - x) > 8) avbrytTrykk();
    };
    document.addEventListener("pointermove", move);
    trykk.current = {
      move,
      timer: window.setTimeout(() => {
        if (trykk.current === null) return;
        avbrytTrykk();
        setSlettemodus(true);
      }, HOLD_MS),
    };
  }, [slettemodus, avbrytTrykk]);

  useEffect(() => () => { avbrytTrykk(); }, [avbrytTrykk]);

  useEffect(() => {
    if (!slettemodus) return;
    const av = (e: Event): void => {
      /* Papirkurvene paa postene OG krysset paa hurtigknappene. Lukkes
         modusen i det samme trykket, forsvinner maalet under fingeren. */
      if (e.target instanceof Element
        && e.target.closest(".synk__papirkurv, .livelogg__fjern") !== null) return;
      setSlettemodus(false);
    };
    const tast = (e: KeyboardEvent): void => { if (e.key === "Escape") setSlettemodus(false); };
    document.addEventListener("pointerdown", av);
    document.addEventListener("keydown", tast);
    return () => {
      document.removeEventListener("pointerdown", av);
      document.removeEventListener("keydown", tast);
    };
  }, [slettemodus]);

  const trekkTilbake = useCallback(async (id: string): Promise<void> => {
    /*
     * SLETTEMODUS BLIR STAAENDE.
     *
     * Her sto `setSlettemodus(false)` foerst i funksjonen, saa modusen
     * lukket seg for hver eneste post — og skulle man rydde fem, maatte
     * man holde inne fem ganger. Brukeren 02.11.2026: «Gjoer saa den
     * ikke lukker seg for hver post jeg sletter.»
     *
     * Rydding kommer i bunker. Modusen lukkes av et trykk hvor som
     * helst UTENFOR en papirkurv — lytteren under gjoer det — og det er
     * den gesten som hoerer til: man er ferdig naar man ser bort.
     */
    if (!await nettlogg.trekkTilbake(id)) {
      /* «Prøv igjen» var feil svar ni av ti ganger: posten er som regel
         ikke din. Loggen har én forfatter per post, og en annen konto
         må trekke tilbake sin egen — se nettlogg.trekkTilbake. */
      setFeil(
        "Posten ble ikke trukket tilbake. Den er skrevet av en annen konto,"
        + " og bare den som skrev den kan fjerne den.",
      );
      return;
    }
    setFeil("");
    setPoster((p) => p.filter((x) => x.id !== id));
  }, []);

  /* ---- flata ---------------------------------------------------------- */

  if (!klar) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">Åpner loggen …</p>
      </div></div>
    );
  }

  if (anker === null) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">
          Fikk ikke åpnet tidskoden for dagen. Uten den finnes det ingen post
          uten tidskode å skrive.
        </p>
      </div></div>
    );
  }

  const a = anker;

  return (
    <div className="flate">
      <div className="flate__innhold livelogg">
        {feil === "" ? null : <p className="synk__melding synk__melding--feil">{feil}</p>}

        {iDag ? null : (
          <p className="synk__melding">
            Du leser opptaksdag {opptaksdag}. Her kan du ikke logge — en post
            skrives med klokka nå, og den hører til dagen som går. Trykk datoen
            i toppen for å komme tilbake.
          </p>
        )}

        <section className="loggkort" aria-label="Min logg">
          <header className="loggkort__hode">
            <span className="loggkort__merke mono">{oekt.member.initials}</span>
            {/*
              * TEAMET FOERST, SAA DEN FOERSTE SOM SKREV.
              *
              * Brukeren 02.11.2026: «I liveloggen skal det vaere den
              * foerste som skriver en post som staar som forfatter etter
              * team navnet, siden det ofte ikke er regi som logger der.»
              *
              * Oekta baerer REGIENS navn paa et team, satt av en utloeser
              * — riktig for loggen som helhet, men feil her: dette er
              * flata der man skriver, og da er den foerste posten svaret.
              * Uten team er loggen din egen, og da staar navnet ditt.
              */}
            <span className="loggkort__navn">
              {(oekt.member.unitName ?? "") !== ""
                ? oekt.member.unitName : oekt.member.displayName}
            </span>
            {(oekt.member.unitName ?? "") === "" ? null : (
              <span className="loggkort__lag">
                {nettlogg.foersteForfatter(poster) ?? "ingen har skrevet ennå"}
              </span>
            )}
            <span className="loggkort__alder mono">
              {/* Gesten maa sies. «Trykk hvor som helst for aa lukke» er
                  ikke noe man gjetter seg til, og modusen blir staaende
                  til man gjoer det — se trekkTilbake. */}
              {slettemodus
                ? "Trykk utenfor for å lukke"
                : poster.length === 1 ? "1 post" : `${poster.length} poster`}
            </span>
          </header>

          <div className="samtale livelogg__rull" ref={rull}>
            {poster.length === 0 ? (
              <p className="samtale__tom">
                Ingen poster ennå. Trykk en hurtigknapp, eller skriv under.
              </p>
            ) : poster.map((p, i) => (
              <div className="bolk" key={p.id}>
                {/*
                  * ========  STREKEN ETTER EN LEVERT RAPPORT  ========
                  *
                  * «Rapporten er et oeyeblikksbilde, og loggen kan skrives
                  * videre etterpaa — da staar det to sannheter paa samme
                  * dag. Streken sier hvor skillet gaar.» Se CLAUDE.md.
                  *
                  * IKKE EN LOGGPOST. Loggen er append-only og hver rad har
                  * én forfatter; en systemskrevet linje ville vaert en rad
                  * uten skriver. Dette er bare en strek i flata.
                  */}
                {levert
                  .filter((r) => r.lukketMs !== null
                    && p.wallUtcMs > r.lukketMs
                    && (i === 0 || (poster[i - 1]?.wallUtcMs ?? 0) <= r.lukketMs))
                  .map((r) => (
                    <p className="rapportstrek" key={`strek-${r.versjon}`}>
                      Rapport v{r.versjon} levert {new Date(r.lukketMs ?? 0)
                        .toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                      {" · det under er ikke med"}
                    </p>
                  ))}
                <div className={`synk__rad${slettemodus ? " synk__rad--sletter" : ""}`}>
                  <button
                    type="button"
                    className="synk__papirkurv"
                    onClick={() => { void trekkTilbake(p.id); }}
                    aria-label={`Fjern posten ${tidskode(a, p)}`}
                    tabIndex={slettemodus ? 0 : -1}
                  >
                    ⌫
                  </button>
                  <article
                    className={`post${kanRette(p) ? " post--rettbar" : ""}`}
                    onPointerDown={startTrykk}
                    onPointerUp={() => { avbrytTrykk(); }}
                    onPointerCancel={() => { avbrytTrykk(); }}
                    /*
                     * Et kort trykk aapner rettingen, et langt aapner
                     * papirkurvene. Begge gestene hoerer til posten, og det
                     * er den samme delingen appen har.
                     */
                    onClick={() => {
                      if (slettemodus || !kanRette(p)) return;
                      if (retter?.id === p.id) return;
                      setRetter({ id: p.id, tekst: p.tekst });
                    }}
                  >
                    {p.farge === null ? null : (
                      <span className="post__stripe" style={{ background: p.farge }} />
                    )}
                    <div className="post__hode">
                      <span
                        className={`mono post__stempel${
                          p.tcUsikker ? " post__stempel--usikker" : ""}`}
                      >
                        {tidskode(a, p)}
                      </span>
                      {p.label === "" ? null : (
                        <span className="mono post__etikett">{p.label}</span>
                      )}
                      {/* Hjertet fra redaktoeren. Et merke og ikke en knapp:
                          herfra svarer man ikke, man logger videre. */}
                      {p.markert ? (
                        <span className="post__hjerte" title="Redaktøren har gitt posten et hjerte">
                          ♥
                        </span>
                      ) : null}
                    </div>
                    {/* Markører uten tekst er ferdige poster. «KLAPP» trenger
                        ingen utdyping — se CLAUDE.md. Den som VIL fylle ut,
                        kan: feltet kommer av et trykk, ikke av en mangel. */}
                    {retter?.id === p.id ? (
                      <textarea
                        className="mono post__rett"
                        value={retter.tekst}
                        rows={2}
                        autoFocus
                        aria-label={`Rett posten ${tidskode(a, p)}`}
                        disabled={lagrer}
                        onClick={(e) => { e.stopPropagation(); }}
                        onChange={(e) => {
                          setRetter({
                            id: p.id,
                            tekst: medStoreForkortelser(e.target.value, forkortelser),
                          });
                        }}
                        onBlur={() => { void lagre(); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            void lagre();
                          }
                          /* Escape forlater feltet uten aa lagre. Teksten som
                             sto der staar fortsatt i posten. */
                          if (e.key === "Escape") { e.preventDefault(); setRetter(null); }
                        }}
                      />
                    ) : p.tekst === "" ? (
                      kanRette(p)
                        ? <span className="post__fyll">Trykk for å fylle ut</span>
                        : null
                    ) : (
                      <p className="mono post__tekst">{p.tekst}</p>
                    )}
                  </article>
                </div>

                {/*
                  * REDAKTOERENS SIDE AV SAMTALEN, til hoeyre og i en annen
                  * farge — samme deler som redaktoerens egen loggflate
                  * bruker (`.svar`, `.svar--synk`). Jo mer de to deler, jo
                  * mindre er det aa laere.
                  */}
                {p.kommentarer.map((k) => (
                  <div className="svar" key={k.id}>
                    <p className="svar__tekst">{k.tekst}</p>
                    <span className="svar__hvem">{k.forfatter}</span>
                  </div>
                ))}

                {p.spoersmaal.map((q) => (
                  <div className="svar svar--synk" key={q.id}>
                    <span className="mono svar__etikett">SYNK · {q.deltaker ?? "ALLE"}</span>
                    <p className="svar__tekst">{q.tekst}</p>
                    <span className="svar__hvem">ligger i synklista</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/*
            * HURTIGKNAPPENE RETT OVER SKRIVEFELTET.
            *
            * Der hånda allerede er. På en telefon ligger de på sin egen side
            * fordi skjermen ikke har plass til begge; her har den det, og da
            * er ett blikk nok til å se både hva man kan merke og hva man
            * holder på å skrive.
            */}
          <div className="livelogg__knapper">
            {knappene.map((k) => (
              <span
                className={`livelogg__knappefeste${
                  slettemodus ? " livelogg__knappefeste--sletter" : ""}`}
                key={k.id}
              >
                <button
                  type="button"
                  className="livelogg__knapp"
                  /*
                   * ========  ETT TRYKK, OG SKRIVEFELTET BEHOLDER FOKUS  ========
                   *
                   * Brukeren 02.11.2026, paa iPad: «naar man er i livelogg og
                   * skal trykke paa en hurtiglogg-knapp man har satt, saa maa
                   * man trykke 2 ganger for at den skal registrere posten.»
                   *
                   * Krysset som fjerner knappen vises paa `:hover`. Paa en
                   * berøringsflate finnes ingen hover — Safari lager en: det
                   * foerste trykket gir hover-tilstanden, det andre trykker.
                   * CSS-en har derfor fått `@media (hover: hover)`, og
                   * papirkurvene naas med et langt trykk i stedet, som i appen.
                   *
                   * `preventDefault` i `pointerdown` gjoer det andre brukeren
                   * ba om: «det er fint om skrivefeltet ikke mister fokus,
                   * selv om du trykker paa en hurtiglogg-knapp.» Da flytter
                   * ikke nettleseren fokus, og man kan skrive videre paa
                   * setningen man holdt paa med.
                   */
                  onPointerDown={(e) => {
                    e.preventDefault();
                    knappLest.current = nettlogg.naa();
                    startTrykk(e);
                  }}
                  onPointerUp={() => {
                    /* Har tidsuret alt fyrt — eller ble trykket avbrutt av en
                       rulling — er `trykk` tom, og da var dette ikke et
                       trykk paa knappen. */
                    const kort = trykk.current !== null;
                    avbrytTrykk();
                    if (kort && !slettemodus) markoer(k);
                  }}
                  onPointerCancel={() => { avbrytTrykk(); knappLest.current = null; }}
                  /* Tastaturet: et «klikk» uten peker har `detail === 0`. */
                  onClick={(e) => { if (e.detail === 0) markoer(k); }}
                  disabled={!iDag}
                  aria-label={`Sett markør ${k.etikett}`}
                >
                  {/*
                    * STRIPA ER KNAPPENS FARGE, som i appen — ikke en kant
                    * rundt hele. Den sier hvilken stripe POSTEN får, og det
                    * er det knappen lover: de to står rett over hverandre
                    * i det man trykker.
                    */}
                  <span className="livelogg__stripe" style={{ background: k.farge }} />
                  <span className="mono livelogg__etikett">{k.etikett}</span>
                </button>
                <button
                  type="button"
                  className="livelogg__fjern"
                  onClick={() => fjernKnapp(k.id)}
                  aria-label={`Fjern hurtigknappen ${k.etikett}`}
                  title="Fjern hurtigknappen"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              className="livelogg__ny"
              value={nyKnapp}
              onChange={(e) => { setNyKnapp(e.target.value); }}
              onKeyDown={(e) => { if (e.key === "Enter") leggTilKnapp(); }}
              onBlur={leggTilKnapp}
              placeholder={knappene.length === 0 ? "Lag en hurtigknapp" : "+ knapp"}
              aria-label="Ny hurtigknapp"
            />
          </div>

          <div className="livelogg__skriv">
            <textarea
              className="livelogg__felt"
              value={utkast}
              rows={2}
              disabled={!iDag}
              placeholder={iDag ? "Skriv en loggpost" : "Du leser en annen dag"}
              aria-label="Ny loggpost"
              /* «chr» blir «CHR». Samme regel som i appen, og den ligger
                 i @infoflyt/shared/logg nettopp fordi begge flatene
                 skriver logg. Se migrasjonsfri rettelse 01.11.2026. */
              onChange={(e) => {
                setUtkast(medStoreForkortelser(e.target.value, forkortelser));
              }}
              onKeyDown={(e) => {
                /* Enter sender, skift+enter gir ny linje. Loggposter er korte,
                   og en knapp å treffe for hver linje er en knapp for mye. */
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  ferdig();
                }
              }}
            />
            <button
              type="button"
              className="synk__knapp synk__knapp--hoved"
              onClick={ferdig}
              disabled={utkast.trim() === "" || !iDag}
            >
              Ferdig
            </button>
          </div>
        </section>

      </div>
    </div>
  );
}
